import PropTypes from 'prop-types';
import { Box, keyframes } from '@mui/material';
import { useTheme } from '@mui/material/styles';

const LOGO_URL =
  'https://eu2.contabostorage.com/68e1ac55689b4b99a9a2ccd407d95b2a:branding-absor/logos/absor/v1/small.svg';

const spin = keyframes`
  0%   { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const spinReverse = keyframes`
  0%   { transform: rotate(0deg); }
  100% { transform: rotate(-360deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.55; transform: scale(0.92); }
  50%       { opacity: 1;    transform: scale(1.08); }
`;

/**
 * Unified application loader.
 *
 * Automatically adapts its colours and glow to the current MUI theme mode
 * (dark / light). Always renders centered relative to its parent container.
 *
 * @param {object}  props
 * @param {'small'|'medium'|'large'} [props.size='medium'] - controls ring dimensions
 */
export default function AppLoader({ size = 'medium' }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // ── size map ──────────────────────────────────────────────────────────────
  const sizeMap = {
    small:  { outer: 56, logo: 20 },
    medium: { outer: 88, logo: 34 },
    large:  { outer: 120, logo: 48 },
  };
  const { outer, logo } = sizeMap[size] ?? sizeMap.medium;
  const mid   = Math.round(outer * 0.73);   // inset for middle ring
  const inner = Math.round(outer * 0.46);   // inset for inner / logo ring

  // ── palette ───────────────────────────────────────────────────────────────
  const ring1Color   = isDark ? '#4F8EF7' : '#1565C0';
  const ring2Color   = isDark ? '#A56EFF' : '#6A1B9A';
  const ring3Color   = isDark ? '#38D9A9' : '#00897B';

  const ring1Shadow  = isDark ? `0 0 8px ${ring1Color}99`  : `0 0 5px ${ring1Color}66`;
  const ring2Shadow  = isDark ? `0 0 7px ${ring2Color}99`  : `0 0 4px ${ring2Color}66`;
  const ring3Shadow  = isDark ? `0 0 6px ${ring3Color}99`  : `0 0 3px ${ring3Color}55`;

  const inset = (px) => `${Math.round((outer - px) / 2)}px`;

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '80vh',
      }}
    >
      {/* ── Ring wrapper (relative positioning context) ── */}
      <Box sx={{ position: 'relative', width: outer, height: outer }}>

        {/* Outer ring — clockwise */}
        <Box sx={{
          position: 'absolute',
          inset: 0,
          borderRadius: '50%',
          border: '3px solid transparent',
          borderTopColor: ring1Color,
          borderRightColor: ring1Color,
          animation: `${spin} 1.1s cubic-bezier(0.6,0.2,0.4,0.8) infinite`,
          filter: `drop-shadow(${ring1Shadow})`,
        }} />

        {/* Middle ring — counter-clockwise */}
        <Box sx={{
          position: 'absolute',
          top: inset(mid), left: inset(mid), right: inset(mid), bottom: inset(mid),
          borderRadius: '50%',
          border: '2.5px solid transparent',
          borderTopColor: ring2Color,
          borderLeftColor: ring2Color,
          animation: `${spinReverse} 0.8s cubic-bezier(0.6,0.2,0.4,0.8) infinite`,
          filter: `drop-shadow(${ring2Shadow})`,
        }} />

        {/* Inner ring — clockwise accent */}
        <Box sx={{
          position: 'absolute',
          top: inset(inner), left: inset(inner), right: inset(inner), bottom: inset(inner),
          borderRadius: '50%',
          border: '2px solid transparent',
          borderBottomColor: ring3Color,
          borderRightColor: ring3Color,
          animation: `${spin} 1.4s cubic-bezier(0.6,0.2,0.4,0.8) infinite`,
          filter: `drop-shadow(${ring3Shadow})`,
        }} />

        {/* Logo — centred, pulsing */}
        {/* Outer Box handles centering (no transform), inner span handles the pulse animation */}
        <Box sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Box
            component="span"
            sx={{ animation: `${pulse} 2s ease-in-out infinite`, lineHeight: 0 }}
          >
            <img
              src={LOGO_URL}
              alt="Absor logo"
              width={logo}
              height={logo}
              style={{
                display: 'block',
                filter: isDark ? 'brightness(1)' : 'brightness(0.15)',
              }}
            />
          </Box>
        </Box>

      </Box>
    </Box>
  );
}

AppLoader.propTypes = {
  size: PropTypes.oneOf(['small', 'medium', 'large']),
};
